import { mulberry32 } from '../core/rng';

/**
 * Cyanotype paper: a seamless tile of mottled Prussian blue with pale fibres and specks,
 * generated once and reused as a pattern. Deterministic (seeded), so it never flickers.
 */

const TILE = 384;
let cached: { canvas: HTMLCanvasElement; contrast: boolean } | null = null;

function makeCanvas(w: number, hgt: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = hgt;
  return c;
}

/** Periodic value noise in [0, 1], period `cells` across the tile. */
function periodicNoise(rng: () => number, cells: number): (x: number, y: number) => number {
  const g: number[] = [];
  for (let i = 0; i < cells * cells; i++) g.push(rng());
  const at = (i: number, j: number) =>
    g[(((j % cells) + cells) % cells) * cells + (((i % cells) + cells) % cells)]!;
  const fade = (t: number) => t * t * (3 - 2 * t);
  return (x: number, y: number) => {
    const fx = (x / TILE) * cells;
    const fy = (y / TILE) * cells;
    const i = Math.floor(fx);
    const j = Math.floor(fy);
    const tx = fade(fx - i);
    const ty = fade(fy - j);
    const a = at(i, j) + (at(i + 1, j) - at(i, j)) * tx;
    const b = at(i, j + 1) + (at(i + 1, j + 1) - at(i, j + 1)) * tx;
    return a + (b - a) * ty;
  };
}

export function paperTile(contrast: boolean): HTMLCanvasElement {
  if (cached && cached.contrast === contrast) return cached.canvas;
  const c = makeCanvas(TILE, TILE);
  const ctx = c.getContext('2d')!;
  const rand = mulberry32(19830512);
  if (contrast) {
    ctx.fillStyle = '#081d38';
    ctx.fillRect(0, 0, TILE, TILE);
    cached = { canvas: c, contrast };
    return c;
  }
  // Base colour with two octaves of soft mottling (uneven exposure of a cyanotype).
  const n1 = periodicNoise(rand.next, 6);
  const n2 = periodicNoise(rand.next, 17);
  const img = ctx.createImageData(TILE, TILE);
  const d = img.data;
  for (let y = 0; y < TILE; y++) {
    for (let x = 0; x < TILE; x++) {
      const v = n1(x, y) * 0.65 + n2(x, y) * 0.35 - 0.5;
      const grain = (rand.next() - 0.5) * 0.035;
      const k = 1 + v * 0.16 + grain;
      const o = (y * TILE + x) * 4;
      d[o] = Math.min(255, 17 * k);
      d[o + 1] = Math.min(255, 54 * k);
      d[o + 2] = Math.min(255, 101 * k);
      d[o + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  // Paper fibres: short pale curves, drawn with wrap-around so the tile is seamless.
  const fibre = (
    x: number,
    y: number,
    len: number,
    ang: number,
    bend: number,
    w: number,
    a: number,
  ) => {
    for (const ox of [-TILE, 0, TILE]) {
      for (const oy of [-TILE, 0, TILE]) {
        const x0 = x + ox;
        const y0 = y + oy;
        const x1 = x0 + Math.cos(ang) * len;
        const y1 = y0 + Math.sin(ang) * len;
        if (
          Math.max(x0, x1) < -4 ||
          Math.min(x0, x1) > TILE + 4 ||
          Math.max(y0, y1) < -4 ||
          Math.min(y0, y1) > TILE + 4
        )
          continue;
        ctx.strokeStyle = `rgba(222, 236, 255, ${a})`;
        ctx.lineWidth = w;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.quadraticCurveTo(
          (x0 + x1) / 2 + Math.cos(ang + Math.PI / 2) * bend,
          (y0 + y1) / 2 + Math.sin(ang + Math.PI / 2) * bend,
          x1,
          y1,
        );
        ctx.stroke();
      }
    }
  };
  ctx.lineCap = 'round';
  for (let i = 0; i < 520; i++) {
    fibre(
      rand.next() * TILE,
      rand.next() * TILE,
      6 + rand.next() * 30,
      rand.next() * Math.PI * 2,
      (rand.next() - 0.5) * 8,
      0.35 + rand.next() * 0.6,
      0.025 + rand.next() * 0.05,
    );
  }
  // Specks: a few pale and dark pinpoints.
  for (let i = 0; i < 260; i++) {
    const pale = rand.next() < 0.55;
    ctx.fillStyle = pale
      ? `rgba(230,240,255,${0.04 + rand.next() * 0.08})`
      : `rgba(2,10,24,${0.08 + rand.next() * 0.12})`;
    ctx.beginPath();
    ctx.arc(rand.next() * TILE, rand.next() * TILE, 0.3 + rand.next() * 0.9, 0, Math.PI * 2);
    ctx.fill();
  }
  cached = { canvas: c, contrast };
  return c;
}

/** Fill a rectangle with paper and a gentle vignette (#123b6d centre to #0d2a4f edges). */
export function drawPaper(
  ctx: CanvasRenderingContext2D,
  w: number,
  hgt: number,
  contrast: boolean,
): void {
  const tile = paperTile(contrast);
  const pattern = ctx.createPattern(tile, 'repeat');
  ctx.save();
  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, w, hgt);
  }
  if (!contrast) {
    const g = ctx.createRadialGradient(
      w * 0.45,
      hgt * 0.4,
      Math.min(w, hgt) * 0.1,
      w * 0.5,
      hgt * 0.5,
      Math.hypot(w, hgt) * 0.62,
    );
    g.addColorStop(0, 'rgba(36, 86, 150, 0.22)');
    g.addColorStop(0.55, 'rgba(18, 59, 109, 0)');
    g.addColorStop(1, 'rgba(4, 16, 36, 0.5)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, hgt);
  }
  ctx.restore();
}

/** Paper as a CSS background (data URL), for the document body behind DOM screens. */
export function paperDataUrl(contrast: boolean): string {
  return paperTile(contrast).toDataURL('image/png');
}
